// Строка сетки ведомости: тарифная строка человека, а не человек.
//
// ## Почему строка сетки — тарифная, а не человек
//
// У человека бывает два поста, и это не редкость: контрольный пример K5 — Ванин Н., БПО
// БурСервис, 90 000 при норме 558 (216 часов) и 80 000 при норме 372 (228 часов). Обе строки
// считаются отдельно, у каждой свои часы и своя ставка. Свернув их в одну строку, мы либо
// потеряем одну ставку, либо покажем «152 / 214» в колонке, которую человек сравнивает глазами
// по столбцу.
//
// ## Почему удержания стоят только в первой строке человека
//
// Тот же K5: удержание формы 1 000 проставлено РОВНО ОДИН РАЗ, а не по разу на строку.
// Повторив его во второй строке, мы покажем 2 000 удержанной формы там, где удержана одна
// тысяча, — и итог столбца разойдётся с итогом ведомости вдвое. Поэтому удержания, официальная
// часть и итог живут в первой строке человека, а продолжение честно говорит, куда смотреть.
//
// Порядок строк — тот, что прислал сервер: люди по фамилии, строки внутри человека по
// `sort_order`. Своей сортировки здесь нет намеренно: два порядка на один список однажды
// разойдутся, и человек, сверяющий ведомость с печатной формой, пойдёт строка за строкой не по
// тем строкам.

import type { PayrollEmployeeOut, PayrollRowOut } from '@/shared/api/endpoints/payroll';

export interface SheetRow {
  /** Ключ строки сетки: идентификатор тарифной строки либо самого человека, если строк нет. */
  readonly id: string;
  readonly employee: PayrollEmployeeOut;
  /** Тарифная строка. Пусто — у человека в ведомости нет ни одной строки. */
  readonly row: PayrollRowOut | null;
  /** Первая строка человека: только в ней стоят удержания, официальная часть и итог. */
  readonly isFirstOfEmployee: boolean;
  /** Сколько всего тарифных строк у этого человека. */
  readonly rowsOfEmployee: number;
  /** Номер строки внутри человека, с единицы. */
  readonly indexInEmployee: number;
}

/**
 * Разложить людей с их тарифными строками в плоский список строк сетки.
 *
 * Человек без тарифных строк остаётся в списке одной строкой: у него могут быть удержания и
 * официальная часть, и молча выкинуть его значит потерять деньги, которые ему должны.
 */
export function flattenEmployees(employees: readonly PayrollEmployeeOut[] | undefined): SheetRow[] {
  const строки: SheetRow[] = [];
  for (const человек of employees ?? []) {
    const свои = человек.rows ?? [];
    if (свои.length === 0) {
      строки.push({
        id: человек.id,
        employee: человек,
        row: null,
        isFirstOfEmployee: true,
        rowsOfEmployee: 0,
        indexInEmployee: 1,
      });
      continue;
    }
    свои.forEach((строка, номер) => {
      строки.push({
        id: строка.id,
        employee: человек,
        row: строка,
        isFirstOfEmployee: номер === 0,
        rowsOfEmployee: свои.length,
        indexInEmployee: номер + 1,
      });
    });
  }
  return строки;
}

/**
 * Сумма целых копеек по колонке, как она СЕЙЧАС нарисована в сетке.
 *
 * Нужна только для сверки с итогом сервера и для подвала тех колонок, по которым сервер итога
 * не присылает. Целые копейки складываются точно, но источником истины остаётся сервер: если
 * два числа разойдутся, это надо показать, а не выбрать одно из них молча.
 */
export function sumShown(
  rows: readonly SheetRow[],
  value: (row: SheetRow) => number | null,
): number {
  let итог = 0;
  for (const строка of rows) {
    итог += value(строка) ?? 0;
  }
  return итог;
}
